import {
  Box,
  Grid,
  Paper,
  Stack,
  Group,
  Button,
  Text,
  TextInput,
  Modal,
} from "@mantine/core"
import { useDebouncedCallback } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { IconSearch, IconSettings } from "@tabler/icons-react"
import { useMemo, useState, useEffect } from "react"

import {
  AxesFeatureLabelsMetadata,
  LabelsExplorerResponses,
} from "@costory/types/endpoints/labelsExplorer"

import { useUnsavedChangesBlocker } from "@costory/front/components/common/useUnsavedChangesBlocker"
import {
  SortHeader,
  type SortBy,
  type SortOrder,
} from "@costory/front/pages/LabelsExplorer/components/SortHeader"
import {
  LabelDetails,
  LabelDetailsPlaceholder,
} from "@costory/front/pages/LabelsExplorer/labelDetails"
import { LabelListItem } from "@costory/front/pages/LabelsExplorer/labelListItem"
import {
  useLabelsExplorerMutation,
  useBulkLabelProcessingMutation,
} from "@costory/front/queries/labelsExplorer"

interface LabelsExplorerValuesProps {
  labels: LabelsExplorerResponses.LabelsStatsFullBilling[]
  ingestMode: boolean
  setIngestMode: (ingestMode: boolean) => void
  axesFeatureLabels: {
    transformations: AxesFeatureLabelsMetadata
  }
}

export function LabelsExplorerValues({
  labels,
  ingestMode,
  setIngestMode,
  axesFeatureLabels,
}: LabelsExplorerValuesProps) {
  const { isPending: isSubmitting } = useLabelsExplorerMutation()
  const { mutateAsync: bulkProcessLabels, isPending: isProcessingLabels } =
    useBulkLabelProcessingMutation()

  useUnsavedChangesBlocker({
    isDirtyCallback: () => ingestMode && selectedLabels.length > 0,
  })

  const LAYOUT = {
    CONTAINER_HEIGHT: "calc(100vh - 100px)",
    GRID_HEIGHT: "calc(100vh - 120px)",
    PAPER_HEIGHT: "calc(100vh - 160px)",
  } as const
  const [selectedLabels, setSelectedLabels] = useState<string[]>([])
  const [showWarningModal, setShowWarningModal] = useState(false)

  const handleSubmitIngestion = async (
    labels: LabelsExplorerResponses.LabelsStatsFullBilling[],
  ) => {
    const toIngest = labels.filter((label) =>
      selectedLabels.includes(label.key),
    )

    // Show confirmation modal using Mantine pattern
    modals.openConfirmModal({
      title: "Confirm Label Changes",
      children: (
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to process the following {toIngest.length}{" "}
            label(s)?
          </Text>

          <Box>
            <Text size="sm" fw={500} mb="xs">
              Labels to be processed:
            </Text>
            <Paper withBorder p="xs" mah={200} style={{ overflowY: "auto" }}>
              <Stack gap="xs">
                {toIngest.map((label) => (
                  <Group
                    key={label.key}
                    justify="space-between"
                    p="xs"
                    bg="surfaceHeader"
                    style={{
                      borderRadius: "4px",
                    }}
                  >
                    <Text size="sm" fw={500} c="textPrimary">
                      {label.key}
                    </Text>
                    <Text
                      size="xs"
                      bg={label.ingested ? "red.1" : "green.1"}
                      c={label.ingested ? "red.7" : "green.7"}
                      fw={500}
                      style={{
                        padding: "2px 8px",
                        borderRadius: "12px",
                      }}
                    >
                      {label.ingested ? "Remove" : "Add"}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
          </Box>

          <Text size="xs" c="dimmed">
            {toIngest.some((l) => l.ingested) &&
            toIngest.some((l) => !l.ingested)
              ? "Some labels will be added and some will be removed."
              : toIngest.some((l) => l.ingested)
                ? "All selected labels will be removed."
                : "All selected labels will be added."}
          </Text>
        </Stack>
      ),
      labels: { confirm: "Confirm", cancel: "Cancel" },
      onConfirm: () => handleConfirmProcessing(toIngest),
    })
  }

  const handleConfirmProcessing = async (
    labelsToProcess: LabelsExplorerResponses.LabelsStatsFullBilling[],
  ) => {
    try {
      // Convert selected labels to the format expected by the backend
      const labelsToSend = labelsToProcess.map((label) => ({
        key: label.key,
        description: "",
      }))

      // Send all labels to backend for bulk processing
      await bulkProcessLabels({ labels: labelsToSend })
    } catch (error) {
      console.error("Bulk processing failed:", error)
    } finally {
      setIngestMode(false)
      setSelectedLabels([])
    }
  }

  const handleExitIngestMode = () => {
    if (selectedLabels.length > 0) {
      setShowWarningModal(true)
    } else {
      setIngestMode(false)
      setSelectedLabels([])
    }
  }

  const handleConfirmExit = () => {
    setIngestMode(false)
    setSelectedLabels([])
    setShowWarningModal(false)
  }

  // Reset selected labels when returning from ingest mode
  useEffect(() => {
    if (!ingestMode) {
      setSelectedLabels([])
    }
  }, [ingestMode])

  // Warn user if they try to navigate away with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (ingestMode && selectedLabels.length > 0) {
        e.preventDefault()
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?"
        return "You have unsaved changes. Are you sure you want to leave?"
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [ingestMode, selectedLabels.length])
  const [hoveredLabel, setHoveredLabel] =
    useState<LabelsExplorerResponses.LabelsStatsFullBilling | null>(null)
  const totalCount = labels.length
  const ingestedCount = labels.filter((l) => l.ingested).length
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState<SortBy | null>("cost")
  const [sortOrder, setSortOrder] = useState<SortOrder | null>("desc")

  const debouncedSetHoveredLabel = useDebouncedCallback(
    (label: LabelsExplorerResponses.LabelsStatsFullBilling | null) => {
      setHoveredLabel(label)
    },
    300,
  )

  const handleHoverLabel = (
    label: LabelsExplorerResponses.LabelsStatsFullBilling | null,
  ) => {
    debouncedSetHoveredLabel(label)
  }
  const filteredLabels = useMemo(() => {
    if (searchTerm.length === 0) return labels
    const lowerQuery = searchTerm.toLowerCase()
    return labels.filter((label) =>
      label.key.toLowerCase().includes(lowerQuery),
    )
  }, [labels, searchTerm])
  const handleRightPanelHover = () => {
    debouncedSetHoveredLabel.cancel()
  }

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      if (sortOrder === "asc") {
        setSortOrder("desc")
      } else if (sortOrder === "desc") {
        setSortBy(null)
        setSortOrder(null)
      } else {
        setSortOrder("asc")
      }
    } else {
      setSortBy(column)
      setSortOrder("asc")
    }
  }

  const sortedLabels = useMemo(() => {
    if (!sortBy || !sortOrder) return filteredLabels

    return [...filteredLabels].sort((a, b) => {
      if (sortBy === "cost") {
        const costA = a.total_cost_key_present ?? 0
        const costB = b.total_cost_key_present ?? 0
        return sortOrder === "asc" ? costA - costB : costB - costA
      }
      return sortOrder === "asc"
        ? a.key.localeCompare(b.key)
        : b.key.localeCompare(a.key)
    })
  }, [filteredLabels, sortBy, sortOrder])

  return (
    <Box
      display="flex"
      h={LAYOUT.CONTAINER_HEIGHT}
      style={{
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box mb="sm">
        <Group justify="space-between" align="center">
          <Stack gap={0}>
            <Text size="sm" c="dimmed">
              {ingestMode
                ? `Selected ${selectedLabels.length} labels. Ingested labels will be removed, non-ingested labels will be added.`
                : `${ingestedCount + axesFeatureLabels.transformations.length} of ${totalCount} labels currently ingested. Select labels to add or remove them.`}
            </Text>
            {ingestMode && selectedLabels.length > 0 && (
              <Text size="xs" c="orange">
                ⚠️ You have unsaved changes. Make sure to submit your
                selections.
              </Text>
            )}
          </Stack>
          <Group gap="sm">
            {ingestMode && (
              <Button
                variant="outline"
                onClick={handleExitIngestMode}
                disabled={isSubmitting || isProcessingLabels}
              >
                Cancel
              </Button>
            )}
            <Button
              leftSection={<IconSettings size={18} />}
              onClick={() => {
                if (ingestMode) {
                  handleSubmitIngestion(labels)
                } else {
                  setIngestMode(true)
                }
              }}
              loading={isSubmitting || isProcessingLabels}
              disabled={ingestMode && selectedLabels.length === 0}
            >
              {ingestMode ? "Add Selected Labels" : "Ingest Labels"}
            </Button>
          </Group>
        </Group>
      </Box>

      <Grid style={{ flex: 1, minHeight: 0, height: LAYOUT.GRID_HEIGHT }}>
        <Grid.Col
          span={{ base: 12, lg: 4 }}
          style={{ height: "100%", minHeight: 0 }}
        >
          <Paper
            p="sm"
            h={LAYOUT.PAPER_HEIGHT}
            mih={0}
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Group justify="space-between" px="sm" py="xs">
              <SortHeader
                label="LABELS"
                column="name"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
              <SortHeader
                label="TOTAL COST KEY PRESENT"
                column="cost"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
            </Group>
            <TextInput
              placeholder="Search labels..."
              leftSection={<IconSearch size={16} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              size="sm"
              mb="sm"
            />
            <Box
              style={{
                flex: 1,
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "transparent transparent",
                minHeight: 0,
                maxHeight: LAYOUT.PAPER_HEIGHT,
              }}
            >
              <Stack gap="xs">
                {sortedLabels.map((label) => (
                  <LabelListItem
                    key={label.key}
                    label={label}
                    onHoverLabel={handleHoverLabel}
                    hoveredLabel={hoveredLabel}
                    ingestMode={ingestMode}
                    checked={selectedLabels.includes(label.key)}
                    onCheck={(key, checked) => {
                      setSelectedLabels((prev) =>
                        checked
                          ? [...prev, key]
                          : prev.filter((k) => k !== key),
                      )
                    }}
                    disabled={isSubmitting || isProcessingLabels}
                  />
                ))}
              </Stack>
            </Box>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Box
            style={{
              height: LAYOUT.PAPER_HEIGHT,
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "transparent transparent",
              minHeight: 0,
            }}
            onMouseEnter={handleRightPanelHover}
          >
            {hoveredLabel ? (
              <LabelDetails label={hoveredLabel} />
            ) : (
              <LabelDetailsPlaceholder />
            )}
          </Box>
        </Grid.Col>
      </Grid>

      {/* Warning Modal for Unsaved Changes */}
      <Modal
        opened={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        title="Unsaved Changes"
        size="md"
        classNames={{ content: "custom-modal-content" }}
      >
        <Stack gap="md">
          <Text size="sm">
            You have {selectedLabels.length} label(s) selected but haven&apos;t
            submitted your changes yet.
          </Text>

          <Text size="sm" c="dimmed">
            Are you sure you want to exit without processing these labels? Your
            selections will be lost.
          </Text>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              onClick={() => setShowWarningModal(false)}
            >
              Continue Editing
            </Button>
            <Button color="error" onClick={handleConfirmExit}>
              Exit Without Saving
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
